import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sliders, KeyRound, Sun, Check, Save, Zap, Plug, Loader2 } from "lucide-react";
import { apiGet, protectedPost } from "@/lib/api";
import { AccessGuardrails } from "@/components/AccessGuardrails";
import { cn } from "@/lib/utils";

const PROVIDERS = [
  { id: "openai", label: "OpenAI", hint: "sk-…", models: ["gpt-4o", "gpt-4o-mini", "gpt-4.1", "gpt-4.1-mini"] },
  { id: "anthropic", label: "Anthropic", hint: "sk-ant-…", models: ["claude-sonnet-4-5", "claude-3-7-sonnet-latest", "claude-3-5-sonnet-latest", "claude-3-5-haiku-latest"] },
  { id: "openrouter", label: "OpenRouter", hint: "sk-or-…", models: ["openai/gpt-4o", "anthropic/claude-3.5-sonnet", "google/gemini-2.0-flash-exp", "meta-llama/llama-3.3-70b-instruct", "deepseek/deepseek-chat"] },
  { id: "perplexity", label: "Perplexity", hint: "pplx-…", note: "Web-aware answers with citations", models: ["sonar", "sonar-pro", "sonar-reasoning-pro", "sonar-deep-research"] },
  { id: "omniroute", label: "OmniRoute", hint: "gateway key", note: "Self-hosted gateway on your VPS — set its URL in Models", models: ["auto"] },
];
const IDS = PROVIDERS.map((p) => p.id);
const empty = () => Object.fromEntries(IDS.map((id) => [id, ""]));

function KeyField({ id, label, hint, value, onChange, configured, masked }) {
  return (
    <div className="mb-3">
      <label className="flex items-center justify-between font-mono text-[10px] uppercase tracking-wider text-slate-500 mb-1">
        <span>{label} key</span>
        {configured && <span className="flex items-center gap-1 text-jarvis-green"><Check className="w-3 h-3" /> {masked}</span>}
      </label>
      <input data-testid={`secret-${id}-key`} type="password" value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={configured ? "•••• (leave blank to keep current)" : hint}
        className="w-full bg-black/40 border border-jarvis-cyan/20 rounded-md px-3 py-2 text-sm font-code text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-jarvis-cyan/50" />
    </div>
  );
}

export default function SettingsPage() {
  const [sec, setSec] = useState(null);
  const [keys, setKeys] = useState(empty());
  const [models, setModels] = useState(empty());
  const [provider, setProvider] = useState("openai");
  const [brief, setBrief] = useState({ enabled: false, time: "07:00" });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    apiGet("/secrets").then((s) => {
      setSec(s);
      setProvider(s.provider);
      setModels(Object.fromEntries(IDS.map((id) => [id, s[`${id}_model`] || ""])));
    }).catch(() => {});
    apiGet("/briefing/settings").then(setBrief).catch(() => {});
  }, []);

  const saveSecrets = async () => {
    const body = { provider };
    IDS.forEach((p) => {
      if (keys[p].trim()) body[`${p}_key`] = keys[p].trim();
      if (models[p]) body[`${p}_model`] = models[p];
    });
    try {
      const s = await protectedPost("/secrets", body);
      setSec(s); setKeys(empty());
      toast.success("Credentials saved. Use Test to check the provider connection.");
    } catch (e) {
      toast.error(e.message || "Failed to save. Are you unlocked?");
    }
  };

  const saveBrief = async () => {
    try {
      const r = await protectedPost("/briefing/settings", brief);
      setBrief(r);
      toast.success(r.enabled ? `Morning briefing scheduled for ${r.time}, Sir.` : "Scheduled briefing disabled.");
    } catch (e) {
      toast.error(e.message || "Failed to save.");
    }
  };

  const testConn = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await protectedPost("/secrets/test", { provider });
      setTestResult(r);
      r.ok ? toast.success(`${provider.toUpperCase()}: ${r.message}`) : toast.error(`${provider.toUpperCase()}: ${r.message}`);
    } catch (e) {
      setTestResult({ ok: false, message: e.message || "Test failed" });
      toast.error("Connection test failed.");
    } finally {
      setTesting(false);
    }
  };

  const configuredMap = Object.fromEntries(IDS.map((id) => [id, { configured: sec?.[`${id}_configured`], masked: sec?.[`${id}_key_masked`] }]));

  return (
    <div className="h-full overflow-y-auto p-5 lg:p-6" data-testid="settings-page">
      <div className="flex items-center gap-3 mb-6">
        <Sliders className="w-6 h-6 text-jarvis-cyan" />
        <div>
          <h1 className="font-mono text-xl lg:text-2xl uppercase tracking-[0.25em] font-bold text-jarvis-cyan jv-glow">Settings</h1>
          <p className="font-mono text-[11px] text-slate-500 tracking-widest">AI providers · secrets · morning briefing</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 max-w-5xl">
        {/* Secrets */}
        <div className="jv-panel p-5" data-testid="secrets-card">
          <div className="flex items-center gap-2 mb-1 font-mono text-[12px] uppercase tracking-widest text-jarvis-cyanBright">
            <KeyRound className="w-4 h-4" /> AI Provider Keys
          </div>
          <p className="text-[12px] text-slate-500 mb-4 leading-relaxed">
            Bring your own key — calls bill your provider directly, never Emergent credits. Keys are stored server-side and never returned.
          </p>

          {PROVIDERS.map((p) => (
            <div key={p.id} className="mb-4 pb-4 border-b border-white/5 last:border-0 last:mb-0 last:pb-0">
              <KeyField id={p.id} label={p.label} hint={p.hint}
                value={keys[p.id]} onChange={(v) => setKeys((k) => ({ ...k, [p.id]: v }))}
                configured={configuredMap[p.id].configured} masked={configuredMap[p.id].masked} />
              {p.note && <p className="-mt-2 mb-2 font-ui text-[12px] text-jarvis-cyanBright/50" data-testid={`provider-note-${p.id}`}>{p.note}</p>}
              <label className="block font-mono text-[10px] uppercase tracking-wider text-slate-500 mb-1">{p.label} model</label>
              <input data-testid={`secret-${p.id}-model`} list={`models-${p.id}`} value={models[p.id] || ""}
                onChange={(e) => setModels((m) => ({ ...m, [p.id]: e.target.value }))}
                className="w-full bg-black/40 border border-jarvis-cyan/20 rounded-md px-3 py-2 text-sm font-code text-slate-100 focus:outline-none focus:border-jarvis-cyan/50" />
              <datalist id={`models-${p.id}`}>
                {p.models.map((m) => <option key={m} value={m} />)}
              </datalist>
            </div>
          ))}

          <label className="block font-mono text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Active provider</label>
          <div className="flex gap-2 mb-4">
            {PROVIDERS.map((p) => (
              <button key={p.id} onClick={() => setProvider(p.id)} data-testid={`provider-${p.id}`}
                className={cn("flex-1 font-mono text-[10px] uppercase tracking-wider px-2 py-2 rounded-md border transition-all",
                  provider === p.id ? "border-jarvis-cyan/50 text-jarvis-cyanBright bg-jarvis-cyan/10" : "border-white/10 text-slate-400")}>
                {p.label}
              </button>
            ))}
          </div>

          <div className="flex gap-2">
            <button onClick={saveSecrets} data-testid="secrets-save-btn"
              className="flex-1 flex items-center justify-center gap-2 font-ui text-[12px] font-semibold uppercase tracking-wider py-2.5 border border-jarvis-cyan/40 text-jarvis-cyan bg-jarvis-cyan/10 hover:bg-jarvis-cyan/20 transition-all hud-chamfer">
              <Save className="w-4 h-4" /> Save Credentials
            </button>
            <button onClick={testConn} disabled={testing} data-testid="secrets-test-btn"
              className="flex items-center justify-center gap-2 font-ui text-[12px] font-semibold uppercase tracking-wider px-4 py-2.5 border border-jarvis-green/40 text-jarvis-green bg-jarvis-green/10 hover:bg-jarvis-green/20 transition-all disabled:opacity-50 hud-chamfer">
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plug className="w-4 h-4" />} Test
            </button>
          </div>
          {testResult && (
            <div data-testid="secrets-test-result" className={cn("mt-2 font-num text-[11px]", testResult.ok ? "text-jarvis-green" : "text-jarvis-red")}>
              {testResult.ok ? "✓ " : "✗ "}{testResult.message}
            </div>
          )}

          <div className="mt-3 flex items-center gap-2 font-mono text-[10px]" data-testid="provider-connection-status">
            <Zap className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-slate-400">{sec?.[`${sec.provider}_configured`] ? `Key configured for ${sec.provider.toUpperCase()} — use Test to verify` : "AI not connected — configure the selected provider key"}</span>
          </div>
        </div>

        {/* Morning briefing */}
        <div className="jv-panel p-5 h-fit" data-testid="briefing-card">
          <div className="flex items-center gap-2 mb-1 font-mono text-[12px] uppercase tracking-widest text-jarvis-amber">
            <Sun className="w-4 h-4" /> Morning Briefing
          </div>
          <p className="text-[12px] text-slate-500 mb-4 leading-relaxed">
            A provider-generated summary of measured application-host health and recorded worker jobs. Unconnected services are reported as unavailable; no revenue or security results are invented.
          </p>

          <div className="flex items-center justify-between mb-4">
            <span className="font-mono text-[11px] text-slate-300">Auto-run daily</span>
            <button onClick={() => setBrief((b) => ({ ...b, enabled: !b.enabled }))} data-testid="briefing-toggle"
              className={cn("relative w-11 h-6 rounded-full transition-colors", brief.enabled ? "bg-jarvis-green/30" : "bg-white/10")}>
              <span className={cn("absolute top-0.5 w-5 h-5 rounded-full transition-all", brief.enabled ? "left-[22px] bg-jarvis-green shadow-[0_0_10px_#18c9ff]" : "left-0.5 bg-slate-400")} />
            </button>
          </div>

          <label className="block font-mono text-[10px] uppercase tracking-wider text-slate-500 mb-1">Time (24h, your device time)</label>
          <input type="time" value={brief.time} onChange={(e) => setBrief((b) => ({ ...b, time: e.target.value }))} data-testid="briefing-time"
            className="w-full mb-4 bg-black/40 border border-jarvis-amber/25 rounded-md px-3 py-2 text-sm font-code text-slate-100 focus:outline-none focus:border-jarvis-amber/50" />

          <button onClick={saveBrief} data-testid="briefing-save-btn"
            className="w-full flex items-center justify-center gap-2 font-mono text-[11px] uppercase tracking-wider py-2.5 rounded-md bg-jarvis-amber/15 border border-jarvis-amber/40 text-jarvis-amber hover:bg-jarvis-amber/25 transition-all">
            <Save className="w-4 h-4" /> Save Schedule
          </button>
          <p className="text-[10px] text-slate-600 mt-3 leading-relaxed">
            Auto-run fires while the JARVIS tab is open at the set time. For always-on delivery, a server scheduler comes in a later phase.
          </p>
        </div>
      </div>
      <AccessGuardrails />
    </div>
  );
}
